import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  FaArrowUp, FaArrowDown, FaEye, FaEyeSlash, FaSave, FaUndo,
  FaThList, FaSlidersH, FaBullhorn, FaTrophy, FaCalendarAlt,
  FaUserFriends, FaCheckCircle, FaVideo, FaImages, FaQuoteLeft,
  FaChartLine, FaRocket, FaBuilding, FaSpinner, FaEnvelope
} from 'react-icons/fa';

interface HomeSection {
  id?: number;
  section_key: string;
  section_name: string;
  description: string;
  sort_order: number;
  is_enabled: number;
  bg_image?: string;
}

const SECTION_ICONS: Record<string, React.ReactNode> = {
  hero_slider: <FaSlidersH className="text-amber-400" />,
  announcement_ticker: <FaBullhorn className="text-[#FDBC12]" />,
  top_rmbians: <FaTrophy className="text-[#FDBC12]" />,
  upcoming_events: <FaCalendarAlt className="text-blue-400" />,
  latest_blogs: <FaBullhorn className="text-[#FDBC12]" />,
  rmbians_meeting: <FaUserFriends className="text-emerald-400" />,
  why_join: <FaRocket className="text-purple-400" />,
  chapter_vision: <FaBuilding className="text-cyan-400" />,
  chapter_teams: <FaUserFriends className="text-indigo-400" />,
  news_media: <FaBullhorn className="text-rose-400" />,
  chapter_gallery: <FaImages className="text-pink-400" />,
  testimonials: <FaQuoteLeft className="text-amber-300" />,
  connect_cta: <FaRocket className="text-[#FDBC12]" />,
  impact_stats: <FaChartLine className="text-emerald-300" />,
  featured_video: <FaVideo className="text-red-400" />,
  get_in_touch: <FaEnvelope className="text-sky-400" />
};

const HomeSectionLayoutAdmin: React.FC = () => {
  const [sections, setSections] = useState<HomeSection[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fetchLayout = async () => {
    setLoading(true);
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/home-section-layout');
      if (res.data.success) {
        setSections(res.data.data);
      }
    } catch (err) {
      console.error('Error loading section layout:', err);
      setMessage({ type: 'error', text: 'Failed to load home section layout configuration.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLayout();
  }, []);

  const handleMove = (index: number, direction: 'UP' | 'DOWN') => {
    if (direction === 'UP' && index === 0) return;
    if (direction === 'DOWN' && index === sections.length - 1) return;

    const newSections = [...sections];
    const targetIndex = direction === 'UP' ? index - 1 : index + 1;

    const temp = newSections[index];
    newSections[index] = newSections[targetIndex];
    newSections[targetIndex] = temp;

    // Recalculate sort_order
    const updated = newSections.map((item, idx) => ({
      ...item,
      sort_order: idx + 1
    }));

    setSections(updated);
  };

  const handleToggleVisibility = (index: number) => {
    const newSections = [...sections];
    newSections[index].is_enabled = newSections[index].is_enabled === 1 ? 0 : 1;
    setSections(newSections);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/home-section-layout', {
        layout: sections
      });
      if (res.data.success) {
        setMessage({ type: 'success', text: res.data.message || 'Section layout saved successfully!' });
        fetchLayout();
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save section layout.' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset the home page section arrangement to default order?')) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/home-section-layout/reset');
      if (res.data.success) {
        setMessage({ type: 'success', text: res.data.message || 'Layout reset to default order!' });
        fetchLayout();
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to reset section layout.' });
    } finally {
      setSaving(false);
    }
  };

  const enabledCount = sections.filter(s => s.is_enabled === 1).length;
  const hiddenCount = sections.filter(s => s.is_enabled === 0).length;

  return (
    <div className="p-4 sm:p-6 bg-slate-50 dark:bg-[#0B1220] min-h-screen text-slate-900 dark:text-slate-100 space-y-6">
      {/* Header Banner */}
      <div className="bg-white dark:bg-[#1E2B45] p-5 sm:p-6 rounded-3xl shadow-sm border border-slate-200 dark:border-[#2A3B5C] flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <span className="text-xs font-black uppercase text-[#FDBC12] tracking-wider flex items-center gap-1.5 mb-1">
            <FaThList /> Home Page Layout Studio
          </span>
          <h1 className="text-xl sm:text-2xl font-black text-[#0A2451] dark:text-white">
            Home Page Section Arrangement Manager
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-1">
            Customize section order, re-arrange positions, and toggle visibility on the chapter homepage in real-time.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center gap-2.5 w-full md:w-auto">
          <button
            type="button"
            onClick={handleReset}
            disabled={saving}
            className="flex-1 md:flex-none px-4 py-2.5 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-white font-extrabold text-xs rounded-xl shadow transition cursor-pointer flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50"
          >
            <FaUndo /> Reset Default
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex-1 md:flex-none px-5 py-2.5 bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black text-xs rounded-xl shadow-lg transition cursor-pointer flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50"
          >
            {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
            Save Arrangement
          </button>
        </div>
      </div>

      {/* Alert Message */}
      {message && (
        <div className={`p-4 rounded-2xl border text-xs font-bold flex items-center gap-2 ${message.type === 'success'
            ? 'bg-emerald-50 dark:bg-emerald-950/80 border-emerald-300 dark:border-emerald-700 text-emerald-800 dark:text-emerald-200'
            : 'bg-rose-50 dark:bg-rose-950/80 border-rose-300 dark:border-rose-700 text-rose-800 dark:text-rose-200'
          }`}>
          <FaCheckCircle className="text-base flex-shrink-0" />
          <span>{message.text}</span>
        </div>
      )}

      {/* Summary Pills */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="bg-white dark:bg-[#1E2B45] p-3.5 rounded-2xl border border-slate-200 dark:border-[#2A3B5C] flex items-center justify-between shadow-xs">
          <span className="text-xs font-bold text-slate-500 dark:text-slate-400">Total Sections</span>
          <span className="text-base font-black text-[#0A2451] dark:text-amber-400">{sections.length}</span>
        </div>

        <div className="bg-emerald-50 dark:bg-emerald-950/40 p-3.5 rounded-2xl border border-emerald-200 dark:border-emerald-800/60 flex items-center justify-between shadow-xs">
          <span className="text-xs font-bold text-emerald-800 dark:text-emerald-300 flex items-center gap-1">
            <FaEye /> Active / Visible
          </span>
          <span className="text-base font-black text-emerald-700 dark:text-emerald-300">{enabledCount}</span>
        </div>

        <div className="bg-rose-50 dark:bg-rose-950/40 p-3.5 rounded-2xl border border-rose-200 dark:border-rose-800/60 flex items-center justify-between shadow-xs col-span-2 sm:col-span-1">
          <span className="text-xs font-bold text-rose-800 dark:text-rose-300 flex items-center gap-1">
            <FaEyeSlash /> Hidden Sections
          </span>
          <span className="text-base font-black text-rose-700 dark:text-rose-300">{hiddenCount}</span>
        </div>
      </div>

      {/* Sections List */}
      <div className="bg-white dark:bg-[#1E2B45] rounded-3xl shadow-md border border-slate-200 dark:border-[#2A3B5C] overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-slate-200 dark:border-[#2A3B5C] flex items-center justify-between">
          <h2 className="font-extrabold text-sm text-[#0A2451] dark:text-white flex items-center gap-2">
            <span>🧩</span> Arrange Section Order (Top to Bottom)
          </h2>
          <span className="text-xs text-slate-400 font-medium">Use Up/Down arrows to reorder</span>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-400 space-y-2">
            <FaSpinner className="animate-spin text-2xl mx-auto text-[#FDBC12]" />
            <p className="text-xs font-bold">Loading Homepage Section Layout...</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-[#2A3B5C]">
            {sections.map((section, idx) => (
              <div
                key={section.section_key || idx}
                className={`p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition ${section.is_enabled === 1
                    ? 'bg-white dark:bg-[#1E2B45] hover:bg-slate-50 dark:hover:bg-[#16233B]'
                    : 'bg-slate-100/70 dark:bg-[#131E32]/70 opacity-60'
                  }`}
              >
                {/* Section Info & Badge */}
                <div className="flex items-start sm:items-center gap-3.5 min-w-0 flex-1">
                  <div className="w-9 h-9 rounded-2xl bg-[#0A2451] dark:bg-[#0B1220] border border-amber-400/30 flex items-center justify-center font-black text-white text-xs flex-shrink-0 shadow">
                    #{idx + 1}
                  </div>

                  <div className="w-10 h-10 rounded-2xl bg-amber-500/10 text-amber-500 border border-amber-400/20 flex items-center justify-center text-lg flex-shrink-0 shadow-inner">
                    {SECTION_ICONS[section.section_key] || <FaThList />}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-extrabold text-sm text-slate-900 dark:text-white truncate">
                        {section.section_name}
                      </h3>
                      {section.is_enabled === 1 ? (
                        <span className="px-2 py-0.5 rounded-md bg-emerald-100 dark:bg-emerald-950/80 text-emerald-800 dark:text-emerald-300 font-extrabold text-[10px] uppercase border border-emerald-300 dark:border-emerald-700">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-md bg-rose-100 dark:bg-rose-950/80 text-rose-800 dark:text-rose-300 font-extrabold text-[10px] uppercase border border-rose-300 dark:border-rose-700">
                          Hidden
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1 font-medium">
                      {section.description}
                    </p>

                    {/* Custom Background Image Editor for Impact Stats & CTA Sections */}
                    {['impact_stats', 'connect_cta', 'why_join'].includes(section.section_key) && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-[10px] font-black uppercase text-[#FDBC12] whitespace-nowrap">
                          🖼️ BG Image:
                        </span>
                        <input
                          type="text"
                          value={section.bg_image || ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setSections(prev => prev.map((s, i) => i === idx ? { ...s, bg_image: val } : s));
                          }}
                          placeholder="Image URL or relative path (e.g. /static/media/...)"
                          className="flex-1 text-xs px-2.5 py-1 rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-[#101729] text-slate-800 dark:text-slate-200 outline-none focus:border-[#FDBC12]"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Control Action Buttons */}
                <div className="flex items-center gap-2 self-end sm:self-center">
                  <button
                    type="button"
                    disabled={idx === 0}
                    onClick={() => handleMove(idx, 'UP')}
                    className="p-2.5 rounded-xl bg-slate-100 dark:bg-[#131E32] hover:bg-amber-500 text-slate-700 dark:text-slate-200 hover:text-[#0A2451] font-bold transition border border-slate-200 dark:border-slate-700 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move Up"
                  >
                    <FaArrowUp size={13} />
                  </button>

                  <button
                    type="button"
                    disabled={idx === sections.length - 1}
                    onClick={() => handleMove(idx, 'DOWN')}
                    className="p-2.5 rounded-xl bg-slate-100 dark:bg-[#131E32] hover:bg-amber-500 text-slate-700 dark:text-slate-200 hover:text-[#0A2451] font-bold transition border border-slate-200 dark:border-slate-700 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move Down"
                  >
                    <FaArrowDown size={13} />
                  </button>

                  <button
                    type="button"
                    onClick={() => handleToggleVisibility(idx)}
                    className={`px-3 py-2.5 rounded-xl font-extrabold text-xs flex items-center gap-1.5 transition cursor-pointer border shadow-sm ${section.is_enabled === 1
                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-500'
                        : 'bg-rose-600 hover:bg-rose-700 text-white border-rose-500'
                      }`}
                  >
                    {section.is_enabled === 1 ? (
                      <>
                        <FaEye size={13} /> Visible
                      </>
                    ) : (
                      <>
                        <FaEyeSlash size={13} /> Hidden
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HomeSectionLayoutAdmin;
